'use client';

import { useTranslations } from 'next-intl';
import { useParams, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

/**
 * Not-found page for activity details. Intentionally minimal — used to keep
 * MainLayout (which pulls AppHeader → HeaderAuthSection → AuthenticatedActions →
 * notification-config, ~23 KiB) out of the activity detail page's bundle graph.
 * The regular detail page renders for the happy path; this only shows on 404.
 *
 * Group-scoped ("tester") activities 404 on the public page because its static
 * render fetches anonymously (on purpose — scoped content must never enter the
 * shared static cache). Before showing the 404 UI, retry the fetch with the
 * viewer's cookies via the browser client and decide:
 *
 *  - fetch fails                 → genuine 404, show the 404 UI
 *  - status ≠ approved           → only the owner/admin can fetch those; send
 *                                  them to /activity/preview (their moderation
 *                                  view, with the revision overlay)
 *  - activity has scopes         → group-restricted viewer → /activity/private
 *  - approved, no scopes         → ambiguous: the activity may still be
 *                                  restricted via the OWNER's groups (effective
 *                                  audience = scopes ∪ owner groups, and owner
 *                                  groups are not exposed in the response), or
 *                                  it may be genuinely public and this 404 came
 *                                  from a child segment (e.g. /reviews behind a
 *                                  feature flag) or a transient server error.
 *                                  Disambiguate with an anonymous probe: public
 *                                  → show the 404 UI, invisible → /private.
 *
 * This boundary is the 404 for the whole /activity/[id] segment (slots,
 * reviews, edit included), so it must never redirect for a publicly visible
 * activity — that's what the probe guards.
 *
 * The activities service is dynamically imported so the API client stays out
 * of this page's initial chunk.
 */
export default function ActivityNotFound() {
  const params = useParams();
  const router = useRouter();
  const locale = (params?.locale as string) || 'en';
  const id = typeof params?.id === 'string' ? params.id : undefined;
  const t = useTranslations('ActivityDrawer');
  const tCommon = useTranslations('Common');

  const [checkingAccess, setCheckingAccess] = useState(!!id);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const resolveAccess = async () => {
      const [{ getActivity }, { getApiUrl }] = await Promise.all([
        import('@website/services/activities.service'),
        import('@website/lib/api/browserFetch')
      ]);

      const activity = await getActivity(id, locale);
      if (cancelled) return;

      if (!activity) {
        setCheckingAccess(false);
        return;
      }

      if (activity.status !== 'approved') {
        router.replace(`/${locale}/activity/preview/${id}`);
        return;
      }

      if (activity.scopes && activity.scopes.length > 0) {
        router.replace(`/${locale}/activity/private/${id}`);
        return;
      }

      // Approved with no activity-level scopes: probe anonymously (cookies
      // omitted — browserFetch always sends them, hence the raw fetch) to tell
      // "restricted via owner groups" apart from "public activity whose 404
      // came from elsewhere".
      let publiclyVisible = true;
      try {
        const probe = await fetch(`${getApiUrl()}/activities/${id}`, { credentials: 'omit' });
        publiclyVisible = probe.ok;
      } catch {
        // Probe unreachable — assume public and show the 404 rather than
        // redirecting to a URL we can't verify.
      }
      if (cancelled) return;

      if (publiclyVisible) {
        setCheckingAccess(false);
      } else {
        router.replace(`/${locale}/activity/private/${id}`);
      }
    };

    resolveAccess().catch(() => {
      if (!cancelled) setCheckingAccess(false);
    });

    return () => {
      cancelled = true;
    };
  }, [id, locale, router]);

  if (checkingAccess) {
    return (
      <div className="min-h-[100dvh] md:min-h-screen flex items-center justify-center bg-white">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-700" />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] md:min-h-screen flex flex-col items-center justify-center bg-white">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('activityNotFound')}</h2>
        <button onClick={() => router.push(`/${locale}/search`)} className="text-primary-600 hover:underline">
          {tCommon('backToSearch')}
        </button>
      </div>
    </div>
  );
}
